import React, { useState, useEffect } from "react";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import { onlyNumber } from "../helpers/formats";
import { optionsForGender, CurrencySymbols, optionsForBusinessCategory } from "../components/common/utlity/options";
import { motion, AnimatePresence } from 'framer-motion';
import { FiChevronRight, FiCheckCircle, FiArrowLeft } from 'react-icons/fi';
import { CgSpinner } from "react-icons/cg";
import { TextField, MenuItem } from "@mui/material";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import { Country, State } from "country-state-city";

// ==================== Common Styles (same as App.js login page) ====================
const getTextFieldSx = (theme) => ({
    "& .MuiInputLabel-root": {
        color: theme === "dark" ? "#9ca3af" : undefined,
    },
    "& .MuiOutlinedInput-root": {
        borderRadius: "12px",
        backgroundColor: theme === "dark" ? "#374151" : "#f9fafb",
        color: theme === "dark" ? "#f3f4f6" : "#111827",
        "& fieldset": {
            borderColor: theme === "dark" ? "#4b5563" : "#d1d5db",
        },
        "&:hover fieldset": {
            borderColor: "#3b82f6",
        },
        "&.Mui-focused fieldset": {
            borderColor: "#3b82f6",
            borderWidth: "2px",
        },
    },
    "& .MuiFormHelperText-root": {
        color: theme === "dark" ? "#ef4444" : undefined,
    },
});

// ==================== Themed TextField (exactly like App.js Email Address field) ====================
const ThemedTextField = ({ theme, children, ...props }) => (
    <motion.div whileFocus={{ scale: 1.01 }} className="relative">
        <TextField
            fullWidth
            variant="outlined"
            size="medium"
            className="mb-2"
            InputProps={{
                className: theme === "dark" ? "text-gray-100" : "",
            }}
            SelectProps={{
                MenuProps: {
                    PaperProps: {
                        sx: {
                            backgroundColor: theme === "dark" ? "#374151" : "#ffffff",
                            color: theme === "dark" ? "#f3f4f6" : "#111827",
                            "& .MuiMenuItem-root": {
                                "&:hover": {
                                    backgroundColor: theme === "dark" ? "#4b5563" : "#f3f4f6",
                                },
                                "&.Mui-selected": {
                                    backgroundColor: theme === "dark" ? "#1e3a5f" : "#e0e7ff",
                                    "&:hover": {
                                        backgroundColor: theme === "dark" ? "#1e3a5f" : "#c7d2fe",
                                    },
                                },
                            },
                        },
                    },
                },
            }}
            sx={getTextFieldSx(theme)}
            {...props}
        >
            {children}
        </TextField>
    </motion.div>
);

// ==================== Password Field (like App.js Password field with eye toggle) ====================
const ThemedPasswordField = ({ theme, showPassword, toggleVisibility, ...props }) => (
    <motion.div whileFocus={{ scale: 1.01 }} className="relative">
        <TextField
            fullWidth
            variant="outlined"
            size="medium"
            className="mb-2"
            type={showPassword ? "text" : "password"}
            InputProps={{
                className: theme === "dark" ? "text-gray-100" : "",
                endAdornment: (
                    <motion.button
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.9 }}
                        type="button"
                        onClick={toggleVisibility}
                        className={`p-1 ${theme === "dark" ? "text-gray-400 hover:text-blue-300" : "text-gray-400 hover:text-blue-600"}`}
                    >
                        {showPassword ? <FaEyeSlash /> : <FaEye />}
                    </motion.button>
                ),
            }}
            sx={getTextFieldSx(theme)}
            {...props}
        />
    </motion.div>
);

// ==================== Validation Schemas ====================
const step1ValidationSchema = Yup.object({
    firstName: Yup.string()
        .max(15, "Must be 15 characters or less")
        .required("First Name is required"),
    lastName: Yup.string()
        .max(20, "Must be 20 characters or less")
        .required("Last Name is required"),
    email: Yup.string()
        .email("Invalid email address")
        .required("Email is required"),
    phone: Yup.string()
        .matches(/^[0-9]{10}$/, "Phone number must be 10 digits")
        .required("Phone number is required"),
    password: Yup.string()
        .min(8, "Password must be at least 8 characters")
        .required("Password is required"),
    confirmPassword: Yup.string()
        .oneOf([Yup.ref("password"), null], "Passwords must match")
        .required("Confirm password is required"),
    gstNumber: Yup.string()
        .matches(
            /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/,
            "Invalid GST Number"
        ),
    address: Yup.string()
        .max(200, "Must be 200 characters or less")
        .required("Address is required"),
    city: Yup.string()
        .max(50, "Must be 50 characters or less")
        .required("City is required"),
    state: Yup.string()
        .max(50, "Must be 50 characters or less")
        .required("State is required"),
    pincode: Yup.string()
        .matches(/^[0-9]{6}$/, "Pincode must be 6 digits")
        .required("Pincode is required"),
});

const step2ValidationSchema = Yup.object({
    businessName: Yup.string().required("Business Name is required"),
    businessCategory: Yup.string().required("Business Category is required"),
    businessOtherCategory: Yup.string().when("businessCategory", {
        is: "Other",
        then: (schema) => schema.required("Please specify your business category"),
        otherwise: (schema) => schema.notRequired(),
    }),
});

// ==================== Main Component ====================
const ResellerSignupForm = ({ handleResellerSignup, onBack, theme }) => {
    const [currentStep, setCurrentStep] = useState(1);
    const [showeyePassword, setShoweyePassword] = useState(false);
    const [showeyeconfirmPassword, setShoweyeconfirmPassword] = useState(false);
    const [countries, setCountries] = useState([]);
    const [states, setStates] = useState([]);

    useEffect(() => {
        const countryList = Country.getAllCountries().map((country) => ({
            label: country.name,
            value: country.isoCode,
        }));
        const stateList = State.getStatesOfCountry("IN").map((state) => ({
            label: state.name,
            value: state.isoCode,
        }));
        setCountries(countryList);
        setStates(stateList);
    }, []);

    const initialValuesForReseller = () => ({
        firstName: "",
        lastName: "",
        gender: "male",
        email: "",
        phone: "",
        gstNumber: "",
        address: "",
        city: "",
        state: "",
        pincode: "",
        password: "",
        confirmPassword: "",
        businessName: "",
        businessCategory: "no_select",
        businessOtherCategory: "",
        country: "IN",
        currency: "INR",
    });

    const handleCountryChange = (setFieldValue, countryIsoCode) => {
        setFieldValue("country", countryIsoCode);
        const stateList = State.getStatesOfCountry(countryIsoCode || "IN").map(
            (state) => ({
                label: state.name,
                value: state.isoCode,
            })
        );
        setStates(stateList);
        setFieldValue("state", "");
    };

    const handleNext = async (values, { setTouched, validateForm }) => {
        const errors = await validateForm();
        const step1Fields = ['firstName', 'lastName', 'email', 'phone', 'password', 'confirmPassword', 'address', 'city', 'state', 'pincode'];
        const step1Errors = Object.keys(errors).filter(key => step1Fields.includes(key));

        if (step1Errors.length === 0) {
            setCurrentStep(2);
        } else {
            const touched = {};
            step1Fields.forEach(field => touched[field] = true);
            setTouched(touched);
        }
    };

    const onSubmit = async (values, { setSubmitting }) => {
        if (currentStep === 1) return;
        console.log("values of resller registartion", values);
        await handleResellerSignup(values);
        setSubmitting(false);
    };

    return (
        <div className='w-full'>
            <Formik
                enableReinitialize={false}
                initialValues={initialValuesForReseller()}
                validationSchema={currentStep === 1 ? step1ValidationSchema : step2ValidationSchema}
                onSubmit={onSubmit}
            >
                {(formik) => {
                    const {
                        errors,
                        values,
                        touched,
                        handleBlur,
                        handleChange,
                        setFieldValue,
                        isSubmitting,
                        setTouched,
                        validateForm,
                    } = formik;

                    return (
                        <Form>
                            <div className="max-h-[50vh] overflow-y-auto pr-2 custom-scrollbar mt-1.5">
                                <AnimatePresence mode="wait">
                                    {currentStep === 1 ? (
                                        <motion.div
                                            key="step1"
                                            initial={{ opacity: 0, x: -20 }}
                                            animate={{ opacity: 1, x: 0 }}
                                            exit={{ opacity: 0, x: -20 }}
                                            className="space-y-4"
                                        >
                                            {/* Name Fields */}
                                            <div className="grid grid-cols-2 gap-4 mt-1.5">
                                                <ThemedTextField
                                                    name="firstName"
                                                    label="First Name"
                                                    value={values.firstName}
                                                    onChange={handleChange}
                                                    onBlur={handleBlur}
                                                    error={touched.firstName && !!errors.firstName}
                                                    helperText={touched.firstName && errors.firstName}
                                                    theme={theme}
                                                />
                                                <ThemedTextField
                                                    name="lastName"
                                                    label="Last Name"
                                                    value={values.lastName}
                                                    onChange={handleChange}
                                                    onBlur={handleBlur}
                                                    error={touched.lastName && !!errors.lastName}
                                                    helperText={touched.lastName && errors.lastName}
                                                    theme={theme}
                                                />
                                            </div>

                                            {/* Gender & Phone */}
                                            <div className="grid grid-cols-2 gap-4">
                                                <ThemedTextField
                                                    select
                                                    name="gender"
                                                    label="Gender"
                                                    value={values.gender}
                                                    onChange={handleChange}
                                                    theme={theme}
                                                >
                                                    {optionsForGender.map(option => (
                                                        <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                                                    ))}
                                                </ThemedTextField>
                                                <ThemedTextField
                                                    name="phone"
                                                    label="Phone Number"
                                                    value={values.phone}
                                                    onChange={handleChange}
                                                    onKeyPress={onlyNumber}
                                                    onBlur={handleBlur}
                                                    error={touched.phone && !!errors.phone}
                                                    helperText={touched.phone && errors.phone}
                                                    inputProps={{ maxLength: 10 }}
                                                    theme={theme}
                                                />
                                            </div>

                                            {/* Email */}
                                            <ThemedTextField
                                                name="email"
                                                label="Email Address"
                                                type="email"
                                                value={values.email}
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                                error={touched.email && !!errors.email}
                                                helperText={touched.email && errors.email}
                                                theme={theme}
                                            />

                                            {/* Passwords */}
                                            <div className="grid grid-cols-2 gap-4">
                                                <ThemedPasswordField
                                                    name="password"
                                                    label="Password"
                                                    value={values.password}
                                                    onChange={handleChange}
                                                    onBlur={handleBlur}
                                                    error={touched.password && !!errors.password}
                                                    helperText={touched.password && errors.password}
                                                    showPassword={showeyePassword}
                                                    toggleVisibility={() => setShoweyePassword(prev => !prev)}
                                                    theme={theme}
                                                />
                                                <ThemedPasswordField
                                                    name="confirmPassword"
                                                    label="Confirm Password"
                                                    value={values.confirmPassword}
                                                    onChange={handleChange}
                                                    onBlur={handleBlur}
                                                    error={touched.confirmPassword && !!errors.confirmPassword}
                                                    helperText={touched.confirmPassword && errors.confirmPassword}
                                                    showPassword={showeyeconfirmPassword}
                                                    toggleVisibility={() => setShoweyeconfirmPassword(prev => !prev)}
                                                    theme={theme}
                                                />
                                            </div>

                                            {/* GST Number */}
                                            <ThemedTextField
                                                name="gstNumber"
                                                label="GST Number (Optional)"
                                                value={values.gstNumber}
                                                onChange={(e) => setFieldValue('gstNumber', e.target.value.toUpperCase())}
                                                onBlur={handleBlur}
                                                error={touched.gstNumber && !!errors.gstNumber}
                                                helperText={touched.gstNumber && errors.gstNumber}
                                                theme={theme}
                                            />

                                            {/* Address */}
                                            <ThemedTextField
                                                multiline
                                                rows={2}
                                                name="address"
                                                label="Address"
                                                value={values.address}
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                                error={touched.address && !!errors.address}
                                                helperText={touched.address && errors.address}
                                                theme={theme}
                                            />

                                            {/* City & State */}
                                            <div className="grid grid-cols-2 gap-4">
                                                <ThemedTextField
                                                    name="city"
                                                    label="City"
                                                    value={values.city}
                                                    onChange={handleChange}
                                                    onBlur={handleBlur}
                                                    error={touched.city && !!errors.city}
                                                    helperText={touched.city && errors.city}
                                                    theme={theme}
                                                />
                                                <ThemedTextField
                                                    name="state"
                                                    label="State"
                                                    value={values.state}
                                                    onChange={handleChange}
                                                    onBlur={handleBlur}
                                                    error={touched.state && !!errors.state}
                                                    helperText={touched.state && errors.state}
                                                    theme={theme}
                                                />
                                            </div>

                                            {/* Pincode */}
                                            <ThemedTextField
                                                name="pincode"
                                                label="Pincode"
                                                value={values.pincode}
                                                onChange={handleChange}
                                                onKeyPress={onlyNumber}
                                                onBlur={handleBlur}
                                                error={touched.pincode && !!errors.pincode}
                                                helperText={touched.pincode && errors.pincode}
                                                inputProps={{ maxLength: 6 }}
                                                theme={theme}
                                            />
                                        </motion.div>
                                    ) : (
                                        <motion.div
                                            key="step2"
                                            initial={{ opacity: 0, x: 20 }}
                                            animate={{ opacity: 1, x: 0 }}
                                            exit={{ opacity: 0, x: 20 }}
                                            className="space-y-4 mt-1.5"
            style={{ marginTop: "1%" }}

                                        >
                                            {/* Business Name */}
                                            <ThemedTextField
                                                name="businessName"
                                                label="Business Name"
                                                value={values.businessName}
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                                error={touched.businessName && !!errors.businessName}
                                                helperText={touched.businessName && errors.businessName}
                                                theme={theme}
                                            />

                                            {/* Business Category */}
                                            <ThemedTextField
                                                select
                                                name="businessCategory"
                                                label="Business Category"
                                                value={values.businessCategory}
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                                error={touched.businessCategory && !!errors.businessCategory}
                                                helperText={touched.businessCategory && errors.businessCategory}
                                                theme={theme}
                                            >
                                                {optionsForBusinessCategory.map(option => (
                                                    <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                                                ))}
                                            </ThemedTextField>

                                            {/* Other Category */}
                                            {values.businessCategory === "Other" && (
                                                <ThemedTextField
                                                    name="businessOtherCategory"
                                                    label="Specify Category"
                                                    value={values.businessOtherCategory}
                                                    onChange={handleChange}
                                                    onBlur={handleBlur}
                                                    error={touched.businessOtherCategory && !!errors.businessOtherCategory}
                                                    helperText={touched.businessOtherCategory && errors.businessOtherCategory}
                                                    theme={theme}
                                                />
                                            )}

                                            {/* Country & Currency */}
                                            <div className="grid grid-cols-2 gap-4">
                                                <ThemedTextField
                                                    select
                                                    name="country"
                                                    label="Country"
                                                    value={values.country}
                                                    onChange={(e) => handleCountryChange(setFieldValue, e.target.value)}
                                                    theme={theme}
                                                >
                                                    {countries.map(option => (
                                                        <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                                                    ))}
                                                </ThemedTextField>
                                                <ThemedTextField
                                                    select
                                                    name="currency"
                                                    label="Currency"
                                                    value={values.currency}
                                                    onChange={handleChange}
                                                    theme={theme}
                                                >
                                                    {CurrencySymbols.map(option => (
                                                        <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                                                    ))}
                                                </ThemedTextField>
                                            </div>
                                        </motion.div>
                                    )}
                                </AnimatePresence>
                            </div>

                            <div className="mt-8 space-y-4">
                                {currentStep === 1 ? (
                                    <motion.button
                                        whileHover={{ scale: 1.02 }}
                                        whileTap={{ scale: 0.98 }}
                                        type='button'
                                        onClick={() => handleNext(values, { setTouched, validateForm })}
                                        className='w-full bg-blue-600 text-white font-semibold py-4 rounded-xl hover:bg-blue-700 shadow-lg transition-all flex items-center justify-center gap-2'
                                    >
                                        <span>Next Step</span>
                                        <FiChevronRight className="text-xl" />
                                    </motion.button>
                                ) : (
                                    <motion.button
                                        whileHover={{ scale: 1.02 }}
                                        whileTap={{ scale: 0.98 }}
                                        type='submit'
                                        disabled={isSubmitting}
                                        className='w-full bg-blue-600 text-white font-semibold py-4 rounded-xl hover:bg-blue-700 shadow-lg transition-all flex items-center justify-center gap-3 disabled:opacity-50 disabled:cursor-not-allowed group'
                                    >
                                        {isSubmitting ? (
                                            <CgSpinner className='animate-spin text-2xl' />
                                        ) : (
                                            <>
                                                <div className="p-2 bg-white/20 rounded-lg">
                                                    <FiCheckCircle className='text-xl' />
                                                </div>
                                                <span className='text-lg'>Complete Registration</span>
                                            </>
                                        )}
                                    </motion.button>
                                )}

                                <motion.button
                                    whileHover={{ x: -5 }}
                                    type="button"
                                    onClick={currentStep === 1 ? onBack : () => setCurrentStep(1)}
                                    className={`w-full py-2 font-semibold text-sm transition-all flex items-center justify-center gap-2 group ${theme === "dark" ? "text-gray-400 hover:text-gray-300" : "text-gray-600 hover:text-gray-900"}`}
                                >
                                    <FiArrowLeft className="group-hover:translate-x-[-2px] transition-transform" />
                                    <span>{currentStep === 1 ? "Back to Login" : "Previous Step"}</span>
                                </motion.button>
                            </div>
                        </Form>
                    );
                }}
            </Formik>
        </div>
    );
};

export default ResellerSignupForm;
